/**
 * Market Opportunity Slide
 * Why moms: an incredibly valuable part of the economy to reach, and a
 * tractable niche to start with
 */

import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { SlideCanvas } from '@components/press/pitch-deck/slide-canvas';
import { CanvasAccent, CanvasEyebrow, CanvasHeading } from '@components/press/pitch-deck/canvas-header';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-09-23: a market slide in the new style, "good at communicating
 * moms are an incredibly valuable part of the economy to connect with and a
 * tractable niche user group to focus on". The Pre-Seed deck's own; the
 * April deck keeps `MarketSlide` as sent.
 *
 * Two halves. Valuable: what runs through moms, each figure with its
 * source. Tractable: the market as nested circles, each as large as its
 * households, closing from every $100K+ US household down to the
 * beachhead — dual-income California families with kids earning $250K+ —
 * whose tenth is the pre-seed goal (Path to Victory). The households and
 * their derivations are the Market Detail appendix's; a year's revenue is
 * each count at the $17.95 a month subscription.
 *
 * The cards rise in turn and the circles close in on the beachhead; still
 * (reduced motion, or `?capture=1` — how the deck is exported) all of it
 * stands in place.
 */

/** The subscription, a year of it. */
const PER_YEAR = 17.95 * 12;

/** What runs through moms, each figure with its source — primary sources
 *  only (checked 2026-09-23): the often-quoted "moms make 85% of household
 *  purchases" has none, its own populariser calling it "cobbled together".
 *  - $2.6T: BLS Consumer Expenditure Survey 2024, Table 1502 (composition of
 *    consumer unit): married couples whose oldest child is under 6
 *    (4.661M × $105,709) or 6–17 (14.910M × $117,355), and one parent with
 *    a child under 18 (6.161M × $61,857) — 25.7M households, $2.62T, 24.6%
 *    of all 135.76M units' $10.66T. Other households with kids aren't in
 *    it, so it runs low.
 *  - 34.2M, 73.9%: BLS, Employment Characteristics of Families — 2025
 *    (Apr 23, 2026): women with own children under 18, and their labor
 *    force participation (an 11-month average, October 2025 uncollected).
 *  - 63%: the Delegation Gap appendix slide's figure. */
const VALUABLE: ReadonlyArray<{ figure: string; says: string; source: string }> = [
  {
    figure: '$2.6T',
    says: 'Spent each year by US families raising kids, about a quarter of all household spending',
    source: 'BLS Consumer Expenditure Survey, 2024',
  },
  {
    figure: '34.2M',
    says: 'US moms with kids under 18, nearly three in four of them in the workforce',
    source: 'BLS, Employment Characteristics of Families, 2025',
  },
  {
    figure: '63%',
    says: 'Of women in dual-income homes carry more of the mental load, even when both work full time',
    source: 'Weeks & Ruppanner, 2025',
  },
];

type Tier = { key: string; name: string; households: number; who: string; fill: string; ring: string };

/** The market, widest first, each within the one before. Households in
 *  millions (Market Detail's derivations). */
const TIERS: readonly Tier[] = [
  { key: 'tam', name: 'TAM', households: 54.5, who: 'US households earning $100K+', fill: `color-mix(in srgb, ${colors.white} 55%, transparent)`, ring: colors.brand.primaryDark },
  { key: 'sam', name: 'SAM', households: 16, who: 'Families with kids earning $100K+', fill: colors.brand.primary, ring: colors.white },
  { key: 'som', name: 'SOM', households: 10.5, who: 'Dual-income families with kids, $100K+', fill: colors.brand.primaryDark, ring: colors.white },
  { key: 'adjacent', name: 'Adjacent', households: 2.2, who: 'Dual-income families with kids, $250K+', fill: colors.brand.extraDark, ring: colors.white },
  { key: 'beachhead', name: 'Beachhead', households: 0.31, who: 'Dual-income families with kids, $250K+, in California', fill: colors.accent.coral.extraDark, ring: colors.white },
];
const BEACHHEAD = TIERS[TIERS.length - 1];

const households = (millions: number) => (millions >= 1 ? `${millions}M` : `${Math.round(millions * 1000)}K`);
const dollars = (value: number) => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(value >= 1e10 ? 1 : 2)}B`;
  return `$${(value / 1e6).toFixed(value >= 1e8 ? 0 : 1)}M`;
};
/** A tier's revenue a year, every household subscribed. */
const yearly = (tier: Tier) => dollars(tier.households * 1e6 * PER_YEAR);

/** The circles: each as large as its households, standing on one line. */
const R_MAX = 272;
const radius = (tier: Tier) => R_MAX * Math.sqrt(tier.households / TIERS[0].households);

/** When each thing arrives, in seconds after the slide does. */
const cardAt = (i: number) => 0.35 + i * 0.2;
const tierAt = (i: number) => 0.5 + i * 0.28;

export const MarketOpportunitySlide: React.FC = () => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const from = (initial: TargetAndTransition): TargetAndTransition | false => (still ? false : initial);

  return (
    <SlideCanvas>
      <Header
        initial={from({ opacity: 0, y: 20 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE_OUT_EXPO }}
      >
        <CanvasEyebrow>Market</CanvasEyebrow>
        <CanvasHeading>
          Moms run the household <CanvasAccent>economy.</CanvasAccent>
        </CanvasHeading>
      </Header>

      <Valuable>
        <Caption>Valuable to connect with</Caption>
        {VALUABLE.map((item, i) => (
          <Card
            key={item.figure}
            initial={from({ opacity: 0, y: 18 })}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE_OUT_EXPO, delay: cardAt(i) }}
          >
            <Figure>{item.figure}</Figure>
            <div>
              <Says>{item.says}</Says>
              <Source>{item.source}</Source>
            </div>
          </Card>
        ))}
      </Valuable>

      <Tractable>
        <Caption>A tractable niche to start with</Caption>
        <Rings aria-hidden="true">
          {TIERS.map((tier, i) => {
            const r = radius(tier);
            return (
              <Ring
                key={tier.key}
                initial={from({ opacity: 0, scale: 0.6 })}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: EASE_OUT_EXPO, delay: tierAt(i) }}
                style={{
                  width: 2 * r,
                  height: 2 * r,
                  left: R_MAX - r,
                  top: 2 * (R_MAX - r),
                  background: tier.fill,
                  borderColor: tier.ring,
                }}
              />
            );
          })}
          <Halo
            initial={from({ opacity: 0, scale: 0.4 })}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE_OUT_EXPO, delay: tierAt(TIERS.length) }}
            style={{
              width: 2 * radius(BEACHHEAD) + 28,
              height: 2 * radius(BEACHHEAD) + 28,
              left: R_MAX - radius(BEACHHEAD) - 14,
              top: 2 * (R_MAX - radius(BEACHHEAD)) - 14,
            }}
          />
        </Rings>

        <Legend>
          {TIERS.map((tier, i) => (
            <Row
              key={tier.key}
              initial={from({ opacity: 0, x: 14 })}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: EASE_OUT_EXPO, delay: tierAt(i) + 0.15 }}
            >
              <Dot style={{ background: tier.fill }} />
              <div>
                <RowName $focus={tier === BEACHHEAD}>
                  {tier.name} <RowValue>{yearly(tier)} a year</RowValue>
                </RowName>
                <RowCount>{households(tier.households)} households</RowCount>
                <RowWho>{tier.who}</RowWho>
                {tier === BEACHHEAD && (
                  <Goal>Pre-seed goal: 10% of it, {dollars(tier.households * 1e6 * PER_YEAR * 0.1)} ARR</Goal>
                )}
              </div>
            </Row>
          ))}
        </Legend>
      </Tractable>

      <Footnote>
        Households: Census ACS &amp; CPS 2024, FRED · a year&rsquo;s revenue at the $17.95/month subscription ·
        derivations in Market Detail
      </Footnote>
    </SlideCanvas>
  );
};

/* Canvas pixels (see SlideCanvas): the header across the top, then the
   valuable half on the left and the tractable half on the right. */

const Header = styled(motion.div)`
  position: absolute;
  left: 150px;
  top: 76px;
`;

const Caption = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.highlight.light};
  margin-bottom: 18px;
`;

const Valuable = styled.section`
  position: absolute;
  left: 150px;
  top: 250px;
  width: 660px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

/* A figure and what it says, as the level cards on How we build stand. */
const Card = styled(motion.div)`
  display: grid;
  grid-template-columns: 188px 1fr;
  align-items: center;
  column-gap: 22px;
  padding: 26px 30px;
  border-radius: 22px;
  background: ${colors.white};
  box-shadow: 0 14px 28px ${colors.shadowSoft};
`;

const Figure = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 64px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${colors.brand.exxxtraDark};
`;

const Says = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 21px;
  line-height: 1.35;
  color: ${colors.neutral.extraDark};
  text-wrap: pretty;
`;

const Source = styled.div`
  margin-top: 8px;
  font-family: ${typography.fontFamily.small};
  font-size: 14px;
  color: ${colors.highlight.light};
`;

/* Its right edge as far in as the left half's left edge. */
const Tractable = styled.section`
  position: absolute;
  left: 890px;
  right: 150px;
  top: 250px;
`;

/* The circles, standing on one line, the widest the frame. */
const Rings = styled.div`
  position: relative;
  width: ${2 * R_MAX}px;
  height: ${2 * R_MAX}px;
  margin-top: 30px;
`;

const Ring = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  border: 2px solid;
  transform-origin: 50% 100%;
  box-shadow: 0 10px 24px ${colors.shadowSoft};
`;

/* The beachhead, marked. */
const Halo = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  border: 2px dashed ${colors.accent.coral.extraDark};
  transform-origin: 50% 50%;
`;

const Legend = styled.div`
  position: absolute;
  left: ${2 * R_MAX + 36}px;
  right: 0;
  top: 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled(motion.div)`
  display: grid;
  grid-template-columns: 18px 1fr;
  column-gap: 12px;
  align-items: start;
`;

const Dot = styled.span`
  width: 18px;
  height: 18px;
  margin-top: 3px;
  border-radius: 50%;
  border: 2px solid ${colors.brand.primaryDark};
`;

const RowName = styled.div<{ $focus: boolean }>`
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(p) => (p.$focus ? colors.accent.coral.exxxtraDark : colors.brand.exxxtraDark)};
`;

/* A year's revenue beside the name, in plain case. */
const RowValue = styled.span`
  margin-left: 6px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: ${colors.neutral.darkMedium};
`;

const RowCount = styled.div`
  margin-top: 2px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 26px;
  font-weight: 500;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
`;

const RowWho = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  line-height: 1.35;
  color: ${colors.neutral.darkMedium};
`;

const Goal = styled.div`
  display: inline-block;
  margin-top: 8px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: ${colors.accent.coral.exxtraLight};
  border: 1px solid ${colors.accent.coral.light};
  font-family: ${typography.fontFamily.small};
  font-size: 14px;
  font-weight: 700;
  color: ${colors.accent.coral.exxxtraDark};
`;

const Footnote = styled.div`
  position: absolute;
  left: 150px;
  bottom: 48px;
  font-family: ${typography.fontFamily.small};
  font-size: 14px;
  color: ${colors.highlight.light};
`;
